import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { assetService } from "@/services/assetService";
import { marketConfigService } from "@/services/providerService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ADMIN_NAV } from "./nav";
import { formatPrice } from "@/lib/format";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white focus-visible:ring-[#2563EB]";
const MARKETS = ["crypto", "stocks", "forex", "commodities", "indices", "b3"];

export default function AdminAssetsPage() {
  const [assets, setAssets] = useState([]);
  const [limits, setLimits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [market, setMarket] = useState("crypto");
  const [provider, setProvider] = useState("");
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [discoverTerm, setDiscoverTerm] = useState("");
  const [universe, setUniverse] = useState([]);
  const [targetCategory, setTargetCategory] = useState("");

  const load = useCallback(async () => {
    try {
      const [list, lim, cfg, src] = await Promise.all([
        assetService.list({ market, search, provider }),
        assetService.limits(),
        marketConfigService.list(),
        assetService.sources(),
      ]);
      setAssets(list);
      setLimits(lim);
      setSources(src);
      setCategories(cfg.flatMap((m) => m.categories.map((c) => ({ ...c, market: m.market }))));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [market, search, provider]);

  useEffect(() => {
    load();
  }, [load]);

  const runDiscover = useCallback(async (term, targetMarket) => {
    try {
      setUniverse(await assetService.discover(term, targetMarket || market));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [market]);

  const toggle = async (asset, field_, value) => {
    try {
      await assetService.update(asset.id, { [field_]: value });
      toast.success(`${asset.symbol} atualizado`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const changeCategory = async (asset, category) => {
    try {
      await assetService.update(asset.id, { category });
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const bulk = async (visible) => {
    try {
      const r = await assetService.bulkVisibility(selected, visible);
      toast.success(`${r.updated.length} ativos atualizados`);
      r.skipped.forEach((s) => toast.error(`${s.symbol}: ${s.reason}`));
      setSelected([]);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Remover ${asset.symbol} do catálogo?`)) return;
    try {
      await assetService.remove(asset.id);
      toast.success("Ativo removido");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const addAsset = async (row) => {
    const category = targetCategory || categories.find((c) => c.market === market)?.slug;
    if (!category) return toast.error("Crie uma categoria primeiro");
    try {
      await assetService.create({ symbol: row.symbol, name: row.name, market, category, visible: true });
      toast.success(`${row.symbol} adicionado e visível`);
      runDiscover(discoverTerm, market);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const marketLimits = limits.filter((l) => l.market === market);
  const marketCategories = categories.filter((c) => c.market === market);

  return (
    <AppShell nav={ADMIN_NAV} title="Ativos" subtitle="Escolha o que o cliente vê, dentro dos limites de cada categoria">
      <div data-testid="admin-assets-page" className="space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {marketLimits.map((l) => {
            const full = l.asset_limit && l.visible_count >= l.asset_limit;
            return (
              <div key={l.slug} data-testid={`limit-card-${l.slug}`}
                   className={`rounded-md border bg-[#12141A] px-5 py-4 ${full ? "border-[#EF4444]/50" : "border-[#22252F]"}`}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">{l.name}</p>
                <p className="mt-2 font-mono text-lg text-white">
                  {l.visible_count}<span className="text-[#4B5563]">/{l.asset_limit}</span>
                </p>
                <p className="mt-1 font-mono text-[10px] text-[#64748B]">{l.total_count} no catálogo</p>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4B5563]" />
            <Input data-testid="assets-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Procurar ativo" className={`${field} pl-10`} />
          </div>
          <select data-testid="assets-market-filter" value={market} onChange={(e) => setMarket(e.target.value)}
                  className="h-11 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
            {MARKETS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="assets-provider-filter" value={provider} onChange={(e) => setProvider(e.target.value)}
                  className="h-11 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
            <option value="">Todos os provedores</option>
            {sources.map((s) => (
              <option key={s.slug} value={s.slug}>{s.slug} ({s.count})</option>
            ))}
          </select>
          {selected.length ? (
            <div className="flex gap-2">
              <Button onClick={() => bulk(true)} data-testid="bulk-show-button"
                      className="h-11 rounded-md bg-[#10B981] text-xs uppercase tracking-[0.15em] text-[#04150F] hover:bg-[#0EA271]">
                Mostrar ({selected.length})
              </Button>
              <Button onClick={() => bulk(false)} data-testid="bulk-hide-button"
                      className="h-11 rounded-md border border-[#22252F] bg-transparent text-xs uppercase tracking-[0.15em] text-[#94A3B8] hover:text-white">
                Ocultar
              </Button>
            </div>
          ) : null}
          <Button data-testid="add-asset-button"
                  onClick={() => { setAddOpen(true); setTargetCategory(marketCategories[0]?.slug || ""); runDiscover("", market); }}
                  className="ml-auto h-11 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
            <Plus className="mr-2 h-4 w-4" /> Adicionar ativo
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border border-[#22252F] bg-[#12141A]">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-4 py-4"> </th>
                <th className="px-4 py-4">Ativo</th>
                <th className="px-4 py-4">Categoria</th>
                <th className="px-4 py-4 text-right">Preço</th>
                <th className="px-4 py-4 text-right">Rank</th>
                <th className="px-4 py-4">Fonte</th>
                <th className="px-4 py-4 text-center">Visível</th>
                <th className="px-4 py-4 text-center">Negociável</th>
                <th className="px-4 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="assets-table-body">
              {assets.map((a) => (
                <tr key={a.id} data-testid={`asset-row-${a.symbol}`} className="border-b border-[#1A1D25] hover:bg-[#151821]">
                  <td className="px-4 py-3">
                    <input type="checkbox" data-testid={`select-${a.symbol}`} checked={selected.includes(a.id)}
                           onChange={(e) => setSelected(e.target.checked ? [...selected, a.id] : selected.filter((x) => x !== a.id))}
                           className="h-4 w-4 accent-[#2563EB]" />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-mono text-white">{a.symbol}</p>
                    <p className="text-xs text-[#94A3B8]">{a.name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <select data-testid={`category-select-${a.symbol}`} value={a.category || ""}
                            onChange={(e) => changeCategory(a, e.target.value)}
                            className="h-9 rounded-md border border-[#22252F] bg-[#0F1116] px-2 text-xs text-white">
                      {marketCategories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white">{a.price ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-[#94A3B8]">{a.rank ? `#${a.rank}` : "—"}</td>
                  <td className={`px-4 py-3 font-mono text-[10px] uppercase ${a.source === "simulated" ? "text-[#F59E0B]" : "text-[#10B981]"}`}>
                    {a.source}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={a.visible} onCheckedChange={(v) => toggle(a, "visible", v)}
                            data-testid={`toggle-visible-${a.symbol}`} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={a.tradable} onCheckedChange={(v) => toggle(a, "tradable", v)}
                            data-testid={`toggle-tradable-${a.symbol}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(a)} data-testid={`delete-asset-${a.symbol}`}
                            className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {assets.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[#64748B]">
                  {provider
                    ? `Nenhum ativo de ${market} está a ser servido por ${provider} neste momento`
                    : "Nenhum ativo encontrado"}
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl border-[#22252F] bg-[#12141A] text-white" data-testid="add-asset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light tracking-tight">
              Adicionar ativo ao catálogo · {market}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Input data-testid="discover-search-input" value={discoverTerm}
                     onChange={(e) => { setDiscoverTerm(e.target.value); runDiscover(e.target.value, market); }}
                     placeholder="Procurar ativo disponível nos provedores (ex: GOOG, US30, XAUUSD)"
                     className={`${field} flex-1`} />
              <select data-testid="discover-category-select" value={targetCategory}
                      onChange={(e) => setTargetCategory(e.target.value)}
                      className="h-11 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                {marketCategories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="max-h-[380px] overflow-y-auto rounded-md border border-[#22252F]" data-testid="discover-list">
              {universe.map((row) => (
                <div key={row.symbol} data-testid={`discover-row-${row.symbol}`}
                     className="flex items-center justify-between gap-4 border-b border-[#1A1D25] px-4 py-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-white">
                      {row.symbol} {row.rank ? <span className="text-[#64748B]">#{row.rank}</span> : null}
                    </p>
                    <p className="truncate text-xs text-[#94A3B8]">{row.name}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-[#94A3B8]">
                      {formatPrice(row.price)}
                    </span>
                    {row.already_added ? (
                      <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#10B981]">
                        <Eye className="h-3 w-3" /> no catálogo
                      </span>
                    ) : (
                      <button onClick={() => addAsset(row)} data-testid={`add-${row.symbol}`}
                              className="rounded-md border border-[#2563EB]/50 px-3 py-1.5 text-xs text-[#60A5FA] transition-colors hover:bg-[#2563EB]/10">
                        Adicionar
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {universe.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-[#64748B]">
                  Sem resultados — force uma atualização do Price Engine em APIs
                </p>
              ) : null}
            </div>
            <p className="flex items-center gap-2 text-xs text-[#64748B]">
              <EyeOff className="h-3.5 w-3.5" /> Ativos ocultos ficam no catálogo mas não aparecem ao cliente.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
